import { apiFetch } from '@/hooks/useFetch';

// In-memory cache for translations
const translationCache = new Map();

/**
 * Get cache key for a translation
 */
const getCacheKey = (text, targetLang) => `${text}:${targetLang}`;

/**
 * Translate a single text
 */
export const translateText = async (text, targetLang = 'es') => {
  if (!text || targetLang === 'en') return text;
  
  const cacheKey = getCacheKey(text, targetLang);
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }
  
  try {
    const { data, error } = await apiFetch(
      `/api/translate/single?text=${encodeURIComponent(text)}&target_lang=${targetLang}`,
      { method: 'POST' }
    );
    
    if (error) throw new Error('Translation failed');
    
    translationCache.set(cacheKey, data.translation);
    return data.translation;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original on error
  }
};

/**
 * Translate multiple texts in batch
 */
export const translateBatch = async (texts, targetLang = 'es') => {
  if (!texts || texts.length === 0 || targetLang === 'en') {
    return texts.reduce((acc, text) => ({ ...acc, [text]: text }), {});
  }
  
  // Check cache first
  const uncachedTexts = [];
  const results = {};
  
  texts.forEach(text => {
    const cacheKey = getCacheKey(text, targetLang);
    if (translationCache.has(cacheKey)) {
      results[text] = translationCache.get(cacheKey);
    } else {
      uncachedTexts.push(text);
    }
  });
  
  // If all cached, return immediately
  if (uncachedTexts.length === 0) {
    return results;
  }
  
  try {
    const { data, error } = await apiFetch('/api/translate/batch', {
      method: 'POST',
      body: {
        texts: uncachedTexts,
        source_lang: 'en',
        target_lang: targetLang,
      }
    });
    
    if (error) throw new Error('Batch translation failed');
    
    // Cache the results
    Object.entries(data.translations).forEach(([original, translated]) => {
      const cacheKey = getCacheKey(original, targetLang);
      translationCache.set(cacheKey, translated);
      results[original] = translated;
    });
    
    return results;
  } catch (error) {
    console.error('Batch translation error:', error);
    // Return originals on error
    uncachedTexts.forEach(text => {
      results[text] = text;
    });
    return results;
  }
};

/**
 * Translate a phrase object (all definition fields)
 * Supports both ES->EN and EN->ES translation based on targetLang
 */
export const translatePhrase = async (phrase, targetLang = 'es') => {
  if (!phrase) return phrase;
  
  try {
    const { data, error } = await apiFetch(`/api/translate/phrase?target_lang=${targetLang}`, {
      method: 'POST',
      body: phrase
    });
    
    if (error) throw new Error('Phrase translation failed');
    
    return data;
  } catch (error) {
    console.error('Phrase translation error:', error);
    return phrase;
  }
};

/**
 * Clear translation cache
 */
export const clearTranslationCache = () => {
  translationCache.clear();
};

export default {
  translateText,
  translateBatch,
  translatePhrase,
  clearTranslationCache,
};
